import { Link } from "react-router-dom";
import {
  Lock,
  Workflow,
  CheckCircle2,
  ArrowRight,
  Users,
  Building2,
  Clock,
  FileText,
  Fingerprint,
  FileSignature,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

const FEATURES = [
  {
    icon: FileSignature,
    title: "Prepare & send",
    desc: "Drag signature, initials, date, text, and checkbox fields onto any PDF. Assign each field to a specific signer.",
  },
  {
    icon: Workflow,
    title: "Sequential or parallel",
    desc: "Choose the signing order per request — route offer letters by hierarchy, or send NDAs to counterparts in parallel.",
  },
  {
    icon: Fingerprint,
    title: "Tamper-evident audit",
    desc: "Every upload, view, sign, decline, and reminder event is captured with timestamp, IP, and device metadata.",
  },
  {
    icon: Lock,
    title: "Enterprise-grade access",
    desc: "Role-based access with org admins, HR managers, approvers, senders, and viewers. Branded sender experience.",
  },
  {
    icon: Clock,
    title: "Reminders & expiry",
    desc: "Automatic and manual reminders, document expiry rules, and a clean timeline of every bottleneck.",
  },
  {
    icon: CheckCircle2,
    title: "Certificate of completion",
    desc: "Each completed package includes a certificate with document hash, signer identity, and a full event log.",
  },
];

const USE_CASES = [
  { icon: Building2, title: "Client agreements", desc: "MSAs, SOWs, renewals." },
  { icon: Users, title: "HR onboarding", desc: "Offer letters, NDAs, policies." },
  { icon: FileText, title: "Vendor contracts", desc: "Procurement, supplier docs." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 md:py-0 md:h-16 flex flex-col md:flex-row items-center md:justify-between gap-2 md:gap-0">
          {/* Mobile: centered logo on top. Desktop: left logo. */}
          <div className="w-full md:w-auto flex items-center justify-center md:justify-start">
            <Link to="/" className="flex items-center" data-testid="nav-logo">
              <Logo className="h-8" />
            </Link>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#features" className="hover:text-slate-900">Features</a>
            <a href="#use-cases" className="hover:text-slate-900">Use cases</a>
            <a href="#trust" className="hover:text-slate-900">Trust & compliance</a>
          </nav>

          <div className="w-full md:w-auto flex items-center justify-center md:justify-end gap-2">
            <Link to="/login" className="flex-1 md:flex-none">
              <Button
                variant="ghost"
                data-testid="nav-signin-btn"
                className="w-full md:w-auto text-slate-700"
              >
                Sign in
              </Button>
            </Link>
            <Link to="/register" className="flex-1 md:flex-none">
              <Button
                data-testid="nav-signup-btn"
                className="w-full md:w-auto bg-slate-900 text-white hover:bg-slate-800"
              >
                Create workspace
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-36 md:pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-60 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_70%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="col-span-1 min-w-0 lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Enterprise e-signatures, re-imagined for modern teams
            </div>
            <h1
              data-testid="hero-headline"
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-balance"
            >
              Agreements, approved.{" "}
              <span className="text-emerald-600">Signed with certainty.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Attestra is the premium digital agreement platform for legal, HR, and operations
              teams. Upload a document, prepare fields, send to signers, and close the loop with
              a tamper-evident audit trail.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button
                  size="lg"
                  data-testid="hero-primary-cta"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 gap-2"
                >
                  Start signing free <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="hero-secondary-cta"
                  className="h-12 px-6 border-slate-300"
                >
                  Sign in
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <Lock className="h-3.5 w-3.5" /> SOC-ready architecture
              </div>
              <div className="flex items-center gap-2">
                <Fingerprint className="h-3.5 w-3.5" /> Tamper-evident audit
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5" /> Role-based access
              </div>
            </div>
          </div>

          <div className="col-span-1 min-w-0 lg:col-span-5">
            <div className="relative rounded-xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.25)] bg-white p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500">
                  Offer Letter · Senior Engineer
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-emerald-200 bg-emerald-50 text-[11px] font-medium text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Completed
                </span>
              </div>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-4 space-y-2">
                <div className="h-2 bg-slate-200 rounded w-3/4" />
                <div className="h-2 bg-slate-200 rounded w-full" />
                <div className="h-2 bg-slate-200 rounded w-5/6" />
                <div className="h-2 bg-slate-200 rounded w-2/3" />
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                      Signed by
                    </div>
                    <div className="italic text-emerald-700 text-lg font-serif">
                      A. Fernandes
                    </div>
                  </div>
                  <div className="font-mono text-[10px] text-slate-500">
                    2026-02-14 11:42 UTC
                  </div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                {[
                  { label: "Uploaded", who: "K. Patel", done: true },
                  { label: "Approved (CFO)", who: "R. Mehta", done: true },
                  { label: "Signed (Candidate)", who: "A. Fernandes", done: true },
                ].map((s) => (
                  <div key={s.label} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      {s.label}
                    </div>
                    <span className="text-xs text-slate-500">{s.who}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="py-10 border-y border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { k: "tamper-evident", v: "Audit every action" },
            { k: "multi-party", v: "Sequential & parallel" },
            { k: "roles", v: "RBAC out of the box" },
            { k: "mobile-first", v: "Signer experience" },
          ].map((i) => (
            <div key={i.k}>
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                {i.k}
              </div>
              <div className="mt-2 text-sm font-medium text-slate-900">{i.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-medium">
              Platform
            </div>
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Everything you need to close, onboard, and approve.
            </h2>
            <p className="mt-5 text-lg text-slate-600">
              A refined workflow engine built for how legal, HR, and operations teams actually work —
              with the audit trail leadership expects.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-xl border border-slate-200 bg-white p-7 hover:-translate-y-0.5 hover:shadow-md transition"
              >
                <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center">
                  <f.icon className="h-5 w-5 text-emerald-400" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section id="use-cases" className="py-24 bg-[#0A1128] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-medium">
              Use cases
            </div>
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Built for the documents that move your business forward.
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {USE_CASES.map((u) => (
              <div
                key={u.title}
                className="rounded-xl border border-white/10 bg-white/5 p-7 hover:bg-white/10 transition"
              >
                <u.icon className="h-6 w-6 text-emerald-400" />
                <h3 className="mt-5 text-xl font-semibold">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-300">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust section */}
      <section id="trust" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="col-span-1 min-w-0 lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-medium">
              Trust & compliance
            </div>
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Every event, captured.{" "}
              <span className="text-slate-500">Every signer, verified.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600">
              Attestra records who uploaded, prepared, sent, viewed, verified, signed, or declined
              — with exact timestamps, IPs, and device metadata. Completed packages include a
              certificate with a document hash for integrity.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-slate-700">
              {[
                "Immutable event log for every document",
                "Role-based access with org, HR, approver, sender, viewer",
                "Sequential or parallel signing workflows",
                "Identity verification on the signer link",
                "Document integrity via SHA-256 hashing",
              ].map((li) => (
                <li key={li} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  {li}
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-1 min-w-0 lg:col-span-6">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 font-mono text-xs leading-relaxed text-slate-700">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">
                audit_trail.log
              </div>
              {[
                ["2026-02-14T09:02:14Z", "document.uploaded", "K. Patel · 10.0.12.14"],
                ["2026-02-14T09:08:41Z", "document.prepared", "K. Patel · 10.0.12.14"],
                ["2026-02-14T09:10:02Z", "document.sent", "K. Patel · seq=1,2"],
                ["2026-02-14T10:17:55Z", "document.viewed", "A. Fernandes · 98.21.3.8"],
                ["2026-02-14T10:19:12Z", "field.signed", "A. Fernandes · 7 fields"],
                ["2026-02-14T10:19:12Z", "document.completed", "sha256: 9f4a…c0b3"],
              ].map((row) => (
                <div key={row[0] + row[1]} className="flex gap-3">
                  <span className="text-slate-400">{row[0]}</span>
                  <span className="text-emerald-700">{row[1]}</span>
                  <span className="text-slate-500 truncate">{row[2]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Ready to send your first agreement?
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Create your Attestra workspace in under a minute.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link to="/register">
              <Button
                size="lg"
                data-testid="cta-primary"
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-7"
              >
                Create workspace
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="h-12 px-7">
                I already have an account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="py-10 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-3">
            <Logo className="h-6" />
            <span>© {new Date().getFullYear()} Vyastics Technologies</span>
          </div>
          <div className="flex items-center gap-5">
            <a href="#features" className="hover:text-slate-700">Features</a>
            <a href="#use-cases" className="hover:text-slate-700">Use cases</a>
            <a href="#trust" className="hover:text-slate-700">Trust</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
